import { ArrowUpRight, Check, Copy, Download, Eye, Code, Presentation, Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import hljs from 'highlight.js/lib/core';
import xml from 'highlight.js/lib/languages/xml';
import 'highlight.js/styles/atom-one-dark.css';
import { jsPDF } from 'jspdf';

// Register HTML language (xml includes html)
hljs.registerLanguage('html', xml);

export interface PPTPayload {
  title: string;
  totalSlides: number;
  slides: string[]; // Array of complete HTML documents
  assets?: string[];
}

interface PPTMakerViewerProps {
  ppt: PPTPayload;
  isDarkMode?: boolean;
}

// Fixed slide dimensions for consistent scaling
const SLIDE_WIDTH = 1280;
const SLIDE_HEIGHT = 720;

export const PPTMakerViewer: React.FC<PPTMakerViewerProps> = ({ ppt, isDarkMode }) => {
  const navigate = useNavigate();
  const [copiedStates, setCopiedStates] = useState<Record<number, boolean>>({});
  // Track which view each slide is in: 'code' or 'preview'
  const [slideViews, setSlideViews] = useState<Record<number, 'code' | 'preview'>>({});
  const copyTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const codeRefs = useRef<Record<number, HTMLElement | null>>({});
  const slideContainerRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [slideScales, setSlideScales] = useState<Record<number, number>>({});

  const slides = ppt.slides || [];

  // Preprocess slides to add a permissive CSP meta tag for proper rendering in iframe
  const processedSlides = useMemo(() => {
    return slides.map((slideHtml) => {
      // Add a permissive CSP meta tag right after <head> to allow external scripts
      const cspMeta = `<meta http-equiv="Content-Security-Policy" content="default-src * 'unsafe-inline' 'unsafe-eval' data: blob:; script-src * 'unsafe-inline' 'unsafe-eval'; style-src * 'unsafe-inline'; img-src * data: blob: https:; font-src * data:;">`;
      const materialIcons = `<link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">`;
      // Ensure Tailwind is present if not already
      const tailwindScript = slideHtml.includes('cdn.tailwindcss.com') ? '' : `<script src="https://cdn.tailwindcss.com"></script>`;

      const headContent = `${cspMeta}\n    ${materialIcons}\n    ${tailwindScript}`;

      // Insert the tags after the opening <head> tag
      if (slideHtml.includes('<head>')) {
        return slideHtml.replace('<head>', `<head>\n    ${headContent}`);
      } else if (slideHtml.includes('<head ')) {
        return slideHtml.replace(/<head\s[^>]*>/, (match) => `${match}\n    ${headContent}`);
      }
      // If no head tag, add it after DOCTYPE or at the start
      if (slideHtml.includes('<!DOCTYPE')) {
        return slideHtml.replace(/<!DOCTYPE[^>]*>/i, (match) => `${match}\n<head>${headContent}</head>`);
      }
      return `<head>${headContent}</head>${slideHtml}`;
    });
  }, [slides]);

  useEffect(() => {
    return () => {
      // Cleanup copy timers
      Object.values(copyTimers.current).forEach((timer) => clearTimeout(timer));
      copyTimers.current = {};
    };
  }, []);

  // Calculate scale for each slide container
  useEffect(() => {
    const updateScales = () => {
      const newScales: Record<number, number> = {};
      Object.entries(slideContainerRefs.current).forEach(([idxStr, container]) => {
        const idx = parseInt(idxStr, 10);
        if (container) {
          const containerWidth = container.clientWidth;
          newScales[idx] = Math.min(containerWidth / SLIDE_WIDTH, 1);
        }
      });
      setSlideScales(newScales);
    };
    updateScales();
    window.addEventListener('resize', updateScales);
    return () => window.removeEventListener('resize', updateScales);
  }, [slides.length, slideViews]);

  // Apply syntax highlighting when code view is shown
  useEffect(() => {
    Object.entries(codeRefs.current).forEach(([idxStr, el]) => {
      const idx = parseInt(idxStr, 10);
      if (el && slideViews[idx] !== 'preview') {
        try {
          // Use manual highlight with ignoreIllegals to suppress security warnings
          // This is intentional as we're displaying HTML code, not executing it
          const result = hljs.highlight(slides[idx] || '', {
            language: 'html',
            ignoreIllegals: true,
          });
          el.innerHTML = result.value;
          el.classList.add('hljs');
        } catch (e) {
          // Fallback: just show text content
          el.textContent = slides[idx] || '';
        }
      }
    });
  }, [slides, slideViews]);


  // Toggle between code and preview for a specific slide
  const toggleSlideView = (idx: number) => {
    setSlideViews((prev) => ({
      ...prev,
      [idx]: prev[idx] === 'preview' ? 'code' : 'preview',
    }));
  };

  const timestamp = new Date().toISOString().replace(/[-:T]/g, '').split('.')[0];
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const downloadAsPdf = async () => {
    if (isGeneratingPdf) return;
    setIsGeneratingPdf(true);

    // 1. Ensure html2canvas is loaded from CDN if not already available
    const CDN_H2C = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
    if (!(window as any).html2canvas) {
      try {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = CDN_H2C;
          s.async = true;
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      } catch (e) {
        console.error("Failed to load html2canvas from CDN:", e);
        setIsGeneratingPdf(false);
        return;
      }
    }
    const html2canvas = (window as any).html2canvas;

    if (!html2canvas) {
      console.error("html2canvas not available");
      setIsGeneratingPdf(false);
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [SLIDE_WIDTH, SLIDE_HEIGHT]
      });

      const safeTitle = `${(ppt.title || "oreog-presentation").toLowerCase().replace(/[^a-z0-9-_]+/g, "-")}-${timestamp}`;

      // Process slides sequentially
      for (let i = 0; i < processedSlides.length; i++) {
        const slideHtml = processedSlides[i];

        // Create a temporary iframe for rendering
        const iframe = document.createElement('iframe');
        iframe.style.width = `${SLIDE_WIDTH}px`;
        iframe.style.height = `${SLIDE_HEIGHT}px`;
        iframe.style.position = 'fixed';
        iframe.style.left = '-10000px';
        iframe.style.top = '0';
        iframe.style.border = 'none';
        iframe.style.visibility = 'hidden'; // Keep layout but hide from view
        // Note: visibility:hidden might affect some captures, but usually fine for html2canvas if forced. 
        // Better to rely on off-screen positioning.
        iframe.style.visibility = 'visible';
        iframe.style.opacity = '0'; // Use opacity 0 instead of visibility hidden just in case

        document.body.appendChild(iframe);

        const docContent = iframe.contentWindow?.document;
        if (docContent) {
          docContent.open();
          docContent.write(slideHtml);
          docContent.close();
        }

        // Wait for rendering and scripts (Tailwind CDN) to load
        // 2 seconds gives enough buffer for CDN fetch and style calculation
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Capture the iframe body
        if (iframe.contentWindow && iframe.contentWindow.document.body) {
          const body = iframe.contentWindow.document.body;

          // Force background color if transparent to ensure white pages aren't transparent
          const computedStyle = iframe.contentWindow.getComputedStyle(body);
          if (computedStyle.backgroundColor === 'rgba(0, 0, 0, 0)' || computedStyle.backgroundColor === 'transparent') {
            // We can't easily guess. But if it's dark text, we want white bg. 
            // Ideally slide has a background class. Assuming white default if generic.
          }

          const canvas = await html2canvas(body, {
            scale: 2, // 2x scale for better quality
            useCORS: true,
            logging: false,
            width: SLIDE_WIDTH,
            height: SLIDE_HEIGHT,
            windowWidth: SLIDE_WIDTH,
            windowHeight: SLIDE_HEIGHT,
            x: 0,
            y: 0
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.95);

          if (i > 0) doc.addPage([SLIDE_WIDTH, SLIDE_HEIGHT]);
          doc.addImage(imgData, 'JPEG', 0, 0, SLIDE_WIDTH, SLIDE_HEIGHT);
        }

        document.body.removeChild(iframe);
      }

      doc.save(`${safeTitle}.pdf`);
    } catch (err) {
      console.error("PDF Generation Error:", err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };


  const generateId = (length: number): string => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let out = '';
    for (let i = 0; i < length; i++) {
      out += chars[Math.floor(Math.random() * chars.length)];
    }
    return out;
  };

  const openSlidesRoute = () => {
    try {
      const id = generateId(12 + Math.floor(Math.random() * 7));
      navigate(`/slides/${id}`, { state: { ppt } });
    } catch (e) {
      console.error('Failed to open slides route:', e);
    }
  };

  const copyCode = async (code: string, slideIdx: number) => {
    await navigator.clipboard.writeText(code);
    setCopiedStates((prev) => ({ ...prev, [slideIdx]: true }));
    if (copyTimers.current[slideIdx]) {
      clearTimeout(copyTimers.current[slideIdx]);
    }
    copyTimers.current[slideIdx] = setTimeout(() => {
      setCopiedStates((prev) => {
        const next = { ...prev };
        delete next[slideIdx];
        return next;
      });
    }, 1500);
  };

  // Get view for a slide (defaults to 'code')
  const getSlideView = (idx: number): 'code' | 'preview' => {
    return slideViews[idx] || 'code';
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">
          {ppt.title || "AI Presentation"}
          {` • ${slides.length} slides`}
        </div>

        {/* Tooltip wrapper */}
        <div className="relative group">
          <button
            onClick={downloadAsPdf}
            disabled={isGeneratingPdf}
            className="px-3 py-1.5 text-xs rounded-2xl border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </button>

          {/* Tooltip */}
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
            Download
          </div>
        </div>
      </div>


      {/* Slides as CodeBlocks */}
      <div className="space-y-4">
        {slides.map((slideHtml, idx) => {
          const currentView = getSlideView(idx);
          const isPreview = currentView === 'preview';

          return (
            <div
              key={idx}
              className={`relative rounded-lg overflow-hidden border ${isDarkMode ? 'border-border/50 bg-[#292929]' : 'border-gray-200 bg-white'
                }`}
            >
              {/* CodeBlock Header */}
              <div
                className={`flex items-center justify-between px-2 py-1 ${isDarkMode ? 'bg-[#202020]' : 'bg-[#f0f0f0]'
                  }`}
              >
                {/* Left: Page number */}
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs font-medium select-none tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}
                  >
                    {idx + 1}/{slides.length}
                  </span>
                </div>

                {/* Right: Action buttons */}
                <div className="flex gap-1">
                  {/* Toggle Code/Preview button */}
                  <div className="relative group">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 px-2 text-xs ${isDarkMode
                        ? 'text-white hover:bg-white/10'
                        : 'text-black/80 hover:bg-black/5'
                        }`}
                      onClick={() => toggleSlideView(idx)}
                    >
                      {isPreview ? (
                        <>
                          <Code className="h-3.5 w-3.5 mr-1" />
                          Code
                        </>
                      ) : (
                        <>
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          Preview
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Copy button */}
                  <div className="relative group">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 w-7 p-0 ${isDarkMode
                        ? 'text-white hover:bg-white/10'
                        : 'text-black/80 hover:bg-black/5'
                        }`}
                      onClick={() => copyCode(slideHtml, idx)}
                    >
                      {copiedStates[idx] ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                      Copy
                    </div>
                  </div>

                  {/* Open in new tab */}
                  <div className="relative group">
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 w-7 p-0 ${isDarkMode
                        ? 'text-white hover:bg-white/10'
                        : 'text-black/80 hover:bg-black/5'
                        }`}
                      onClick={openSlidesRoute}
                    >
                      <Presentation className="h-3.5 w-3.5" />
                    </Button>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                      Play
                    </div>
                  </div>
                </div>
              </div>

              {/* Content area - toggles between Code and Preview */}
              {isPreview ? (
                /* Inline Preview with iframe using transform:scale() for responsive sizing */
                <div
                  ref={(el) => { slideContainerRefs.current[idx] = el; }}
                  className="relative w-full overflow-hidden"
                  style={{ height: (slideScales[idx] || 0.5) * SLIDE_HEIGHT }}
                >
                  <iframe
                    title={`Slide ${idx + 1} Preview`}
                    className="absolute bg-white rounded-b-lg"
                    srcDoc={processedSlides[idx]}
                    style={{
                      width: SLIDE_WIDTH,
                      height: SLIDE_HEIGHT,
                      transform: `scale(${slideScales[idx] || 0.5})`,
                      transformOrigin: 'top left',
                      border: 'none',
                    }}
                  />
                </div>
              ) : (
                /* Code content with syntax highlighting and thin scrollbars */

                <div
                  className="overflow-auto h-[calc(80vh-200px)]"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: isDarkMode ? '#4a4a4a #2a2a2a' : '#c1c1c1 #f1f1f1',
                  }}
                >
                  <pre className="!m-0 !p-4 !bg-transparent overflow-visible">
                    <code
                      ref={(el) => { codeRefs.current[idx] = el; }}
                      className={`language-html thin-scrollbar !bg-transparent text-sm leading-relaxed hljs`}
                      style={{
                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                        tabSize: 2,
                        scrollbarWidth: 'thin',
                        scrollbarColor: isDarkMode ? '#4a4a4a #2a2a2a' : '#c1c1c1 #f1f1f1',
                      }}
                    >
                      {slideHtml}
                    </code>
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Assets section */}
      {Array.isArray(ppt.assets) && ppt.assets.length > 0 && (
        <div className="mt-6 rounded-lg overflow-hidden w-full">
          {/* <div className="w-full flex items-center justify-between px-4 py-2 text-sm bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <span className="font-semibold">Assets Used ({ppt.assets.length})</span>
          </div>
          <div className="p-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {ppt.assets.map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer" className="block aspect-video overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
                  <img
                    src={u}
                    alt={`asset-${i}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { const el = e.currentTarget; el.style.display = 'none'; }}
                  />
                </a>
              ))}
            </div>
          </div> */}
        </div>
      )}
    </div>
  );
};

export default PPTMakerViewer;
